import { useState, useEffect } from "react";
import { Outlet, Link, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  User, Home, ShoppingBag, Repeat, Wallet, CircleDollarSign, Heart,
  LogOut, ChevronDown, Menu, X, Bell, Download, ShoppingCart,
  PanelLeftClose, PanelLeftOpen, Share2, Store, GraduationCap, Compass, PlayCircle, Presentation, FileText, Award,
} from "lucide-react";
import { useMyAuth } from "../contexts/MyAuthContext";
import { PHOTO_URL, myApi } from "../api";
import AppSwitcher, { AppSwitcherMobile } from "./AppSwitcher";
import CartBadge from "./shop/CartBadge";
import useSidebarCollapsed from "../../shared/useSidebarCollapsed";
import GoogleTranslate from "../../shared/GoogleTranslate";
import usePushSounds from "../../hooks/usePushSounds";

const NAV_BASE = [
  { to: "/my", icon: Home, label: "Início", end: true, testid: "my-nav-home" },
  { to: "/my/perfil", icon: User, label: "Informações Pessoais", testid: "my-nav-profile" },
  { to: "/my/eventos", icon: Presentation, label: "Simulador Proposta", testid: "my-nav-eventos" },
  { to: "/my/carrinho", icon: ShoppingCart, label: "Carrinho", testid: "my-nav-cart" },
  { to: "/my/encomendas", icon: ShoppingBag, label: "Encomendas", testid: "my-nav-orders" },
  { to: "/my/subscricoes", icon: Repeat, label: "Subscrições", testid: "my-nav-subs" },
  { to: "/my/carteira", icon: Wallet, label: "Vales & Prémios", testid: "my-nav-wallet" },
  { to: "/my/comissoes", icon: CircleDollarSign, label: "Comissões", testid: "my-nav-comissoes" },
  { to: "/my/recomendar", icon: Share2, label: "Recomendar", testid: "my-nav-recomendar" },
  { to: "/my/mudardevida", icon: Heart, label: "Mudar de Vida", testid: "my-nav-mv" },
  { to: "/my/coach", icon: Compass, label: "Coach Virtual", testid: "my-nav-coach" },
  { to: "/my/cursos-diplomas", icon: GraduationCap, label: "Cursos e Diplomas", testid: "my-nav-cursos" },
];

const NAV_CONSULTOR_EXTRA = [
  // Catálogo Revenda — exclusivo a consultores (`ID_TipoCliente='CS'`).
  // Aponta para a página interna `/my/revenda` que lista os produtos
  // `ID_TipoProduto='RV'` activos e permite adicionar ao carrinho.
  { to: "/my/revenda", icon: Store, label: "Produtos Revenda", testid: "my-nav-revenda" },
  { to: "/my/downloads", icon: Download, label: "Downloads", testid: "my-nav-downloads" },
  // Notificações — exclusivo a consultores (`ID_TipoCliente='CS'`).
  { to: "/my/notificacoes", icon: Bell, label: "Notificações", testid: "my-nav-notifs" },
];

export default function MyLayout() {
  const { user, loading, logout } = useMyAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed("my_sidebar_collapsed");
  const [isFormador, setIsFormador] = useState(false);
  usePushSounds();

  useEffect(() => {
    let alive = true;
    myApi.formadorInfo().then((d) => { if (alive) setIsFormador(!!d?.is_formador); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="my-root min-h-screen grid place-items-center">
        <div className="w-10 h-10 border-4 border-[#978D78]/30 border-t-[#0F1829] rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) {
    // Preserve the deep link (e.g. /my/cart/add?id=…&q=…) so the user lands
    // back on the same product after logging in. Without this, the WordPress
    // → MYIHTP redirect-to-cart flow would lose the pending product.
    const next = `${location.pathname}${location.search}`;
    const target = next && next !== "/my" && next !== "/my/"
      ? `/login?next=${encodeURIComponent(next)}`
      : "/login";
    return <Navigate to={target} replace />;
  }

  const initials = (user.NomeCompleto || user.email || "?").charAt(0).toUpperCase();
  const isConsultor = !!user.IsConsultor;
  const NAV = [
    ...NAV_BASE,
    ...(isFormador ? [{ to: "/my/area-formador", icon: Award, label: "Área de Formador", testid: "my-nav-area-formador" }] : []),
    ...(isConsultor ? NAV_CONSULTOR_EXTRA : []),
  ];

  return (
    <div className="my-root min-h-screen bg-[#FAF8F3]">
      <AppSwitcher current="my" isConsultor={isConsultor} isDBAdmin={!!user.IsDBAdmin} theme="light" />
      <AppSwitcherMobile current="my" isConsultor={isConsultor} isDBAdmin={!!user.IsDBAdmin} theme="light" />

      {/* Sidebar */}
      <aside
        data-testid="my-sidebar"
        className={`fixed top-0 left-0 lg:left-[72px] z-20 h-screen flex flex-col bg-white border-r border-[#E6E1D6] ${
          mobileNav ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 transition-all duration-300 ${collapsed ? "lg:w-[72px] w-[260px]" : "w-[260px]"}`}
      >
        <div className={`shrink-0 h-20 flex items-center border-b border-[#E6E1D6] ${collapsed ? "lg:px-3 lg:justify-center px-6" : "px-6"}`}>
          <Link to="/my" className="flex items-center gap-3" data-testid="my-logo">
            <div className="w-10 h-10 rounded-full bg-[#0F1829] grid place-items-center text-white shrink-0">
              <span className="font-serif text-lg" style={{ fontFamily: "'Fraunces', serif" }}>
                M
              </span>
            </div>
            <div className={collapsed ? "lg:hidden" : ""}>
              <div
                className="text-lg leading-none text-[#0F1829]"
                style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
              >
                MYIHTP
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] mt-0.5">
                Portal do Cliente
              </div>
            </div>
          </Link>
          <button
            className="lg:hidden ml-auto p-1 text-[#0F1829]"
            onClick={() => setMobileNav(false)}
            data-testid="my-close-nav"
          >
            <X size={20} />
          </button>
        </div>

        <nav className={`flex-1 min-h-0 overflow-y-auto p-4 space-y-1 ${collapsed ? "lg:px-2" : ""}`}>
          {NAV.map((n) => {
            // Itens externos abrem em nova tab (ex.: Produtos Revenda → WordPress)
            if (n.external && n.href) {
              return (
                <a
                  key={n.href}
                  href={n.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={n.testid}
                  title={collapsed ? n.label : undefined}
                  onClick={() => setMobileNav(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-[14px] text-sm transition-all text-[#4B5563] hover:bg-[#F5F2EB] hover:text-[#0F1829] ${collapsed ? "lg:justify-center" : ""}`}
                >
                  <n.icon size={18} strokeWidth={1.6} className="shrink-0" />
                  <span className={collapsed ? "lg:hidden" : ""} style={{ fontFamily: "'Manrope', sans-serif" }}>{n.label}</span>
                </a>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                title={collapsed ? n.label : undefined}
                onClick={() => setMobileNav(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-[14px] text-sm transition-all ${collapsed ? "lg:justify-center" : ""} ${
                    isActive
                      ? "bg-[#0F1829] text-white shadow-[2px_2px_12px_rgba(15,24,41,0.15)]"
                      : "text-[#4B5563] hover:bg-[#F5F2EB] hover:text-[#0F1829]"
                  }`
                }
              >
                <n.icon size={18} strokeWidth={1.6} className="shrink-0" />
                <span className={collapsed ? "lg:hidden" : ""} style={{ fontFamily: "'Manrope', sans-serif" }}>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className={`shrink-0 border-t border-[#E6E1D6] p-4 ${collapsed ? "lg:px-2" : ""}`}>
          <button
            onClick={async () => { await logout(); window.location.href = "/login"; }}
            data-testid="my-logout-btn"
            title={collapsed ? "Terminar sessão" : undefined}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[14px] text-sm text-[#B91C1C] hover:bg-red-50 transition ${collapsed ? "lg:justify-center" : ""}`}
          >
            <LogOut size={18} strokeWidth={1.6} className="shrink-0" />
            <span className={collapsed ? "lg:hidden" : ""}>Terminar sessão</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className={`pl-0 pb-[72px] lg:pb-0 transition-all duration-300 ${collapsed ? "lg:pl-[144px]" : "lg:pl-[332px]"}`}>
        {/* Topbar */}
        <header
          data-testid="my-topbar"
          className="sticky top-0 z-10 h-16 md:h-20 bg-white/80 backdrop-blur-xl border-b border-[#E6E1D6] flex items-center px-4 md:px-6"
        >
          <button
            className="lg:hidden mr-3 p-2 text-[#0F1829]"
            onClick={() => setMobileNav(true)}
            data-testid="my-open-nav"
          >
            <Menu size={22} />
          </button>

          {/* Desktop sidebar toggle */}
          <button
            type="button"
            onClick={toggleCollapsed}
            data-testid="my-sidebar-toggle"
            title={collapsed ? "Expandir menu" : "Encolher menu"}
            className="hidden lg:flex items-center justify-center w-9 h-9 mr-3 rounded-md text-[#0F1829] hover:bg-[#F5F2EB] transition-colors"
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>

          <div className="min-w-0 max-w-[40vw] md:max-w-[36vw] lg:max-w-none">
            <div
              className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#978D78]"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Bem-vindo
            </div>
            <div
              className="text-sm md:text-lg lg:text-xl text-[#0F1829] leading-tight truncate"
              style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
              title={user.NomeCompleto || "Cliente IHTP"}
            >
              {user.NomeCompleto || "Cliente IHTP"}
            </div>
          </div>

          <div className="flex-1" />

          {/* Link externo: voltar ao site institucional */}
          <a
            href="https://ihavethepower.net"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="my-link-site"
            className="inline-flex items-center gap-1.5 mr-2 md:mr-3 px-2.5 md:px-3 py-1.5 md:py-2 text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0F1829] hover:text-black border border-[#E6E1D6] hover:border-[#0F1829] rounded-md transition"
            title="Voltar ao site IHTP"
          >
            Site
          </a>

          <div className="hidden md:block mr-3">
            <GoogleTranslate />
          </div>

          <div className="mr-2">
            <CartBadge />
          </div>

          <div className="relative">
            <button
              data-testid="my-user-menu"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-[#F5F2EB] transition"
            >
              {user.AvatarUrl || user.HasPhoto ? (
                <img
                  src={`${PHOTO_URL}?uid=${user.ID_Cliente}`}
                  alt={user.NomeCompleto}
                  data-testid="my-avatar-img"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const sib = e.currentTarget.nextElementSibling;
                    if (sib) sib.style.display = 'grid';
                  }}
                  className="w-9 h-9 rounded-full object-cover border border-[#E6E1D6]"
                />
              ) : null}
              <div
                data-testid="my-avatar-initial"
                style={{ display: (user.AvatarUrl || user.HasPhoto) ? 'none' : 'grid' }}
                className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0F1829] to-[#978D78] place-items-center text-white font-semibold"
              >
                {initials}
              </div>
              <ChevronDown size={16} className="text-[#6B7280]" />
            </button>
            {menuOpen && (
              <div
                data-testid="my-user-dropdown"
                className="absolute right-0 mt-2 w-60 bg-white rounded-[20px] border border-[#E6E1D6] shadow-xl p-2"
              >
                <div className="px-3 py-2 border-b border-[#F5F2EB] mb-1">
                  <div className="text-sm font-medium text-[#0F1829]">
                    {user.NomeCompleto}
                  </div>
                  <div className="text-xs text-[#6B7280] truncate">{user.Email || user.email}</div>
                  {isConsultor && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] bg-[#F5F2EB] text-[#978D78] uppercase tracking-wider">
                      Consultor
                    </span>
                  )}
                </div>
                <Link
                  to="/my/perfil"
                  onClick={() => setMenuOpen(false)}
                  className="block px-3 py-2 text-sm text-[#0F1829] hover:bg-[#F5F2EB] rounded-lg"
                >
                  Ver perfil
                </Link>
                <button
                  onClick={async () => { await logout(); window.location.href = "/login"; }}
                  className="w-full text-left px-3 py-2 text-sm text-[#B91C1C] hover:bg-red-50 rounded-lg"
                >
                  Terminar sessão
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-10 max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
